import { createReducer } from "reduxsauce"
import { put, takeLatest, select, call, getContext, all } from "redux-saga/effects"
import actionCreator from "helpers/actionCreator"
import { del as delApi } from "api"
import { isEmpty, filter, findIndex, some, cloneDeep, size, concat, get, chain, pick, map, includes } from "lodash"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Creators as SegmentCreators } from "modules/compare-segments-root/model"
import { Types as ServerNotificationsTypes } from "modules/server-notifications/model"
import { getRawTopic } from "modules/research-topic-root/selectors"
import { Types as LoaderTypes, Creators as modelCreators } from "model/data"
import { Types as SaveExperimentTypes } from "modules/save-experiment/model"
import { Types as DialogTypes } from "modules/dialog/model"
import { getUserPreference } from "helpers/userPreferences"
import { updateUploadingExperiments, updateAlternativeExperimentProgress } from "helpers/comparison"
import { FILTERS, NORMALIZATION } from "helpers/segments"
import { getSelectedSettings, getCurrentTopicId, getRawComparisonItems } from "./selectors/model"
import { getEarliestDate, getLatestDate } from "./selectors/dates"
import { v4 as uuid } from "uuid"
import { getInstrumentPortfolio } from "./selectors/instrument"

function updatePreferences(state) {
  return {
    type: "researchTopicSettings",
    value: getSelectedSettings(state),
    key: getCurrentTopicId(state),
  }
}

export const { Types, Creators } = actionCreator("ompnt/modules/researchTopic", {
  loadSavedSettings: ["topicSettings"],
  loadNewExperiment: ["newExperiment"],
  setSelectedComparisonType: {
    props: ["selectedComparisonType"],
    track: true,
    trackType: "research-topic-compare-tab-switch",
    trackPayload: ({ selectedComparisonType }) => ({ tab: selectedComparisonType }),
    updatePreference: {
      getItems: (state, props) => [updatePreferences(state, props)],
    },
  },
  trackExperimentTypeSelection: {
    props: ["experimentType"],
    track: true,
    trackType: "research-topic-comparison-type-selected",
    trackPayload: ({ experimentType }) => ({ type: experimentType }),
  },
  trackAddExperiment: {
    props: [],
    track: true,
    trackType: "research-topic-add-experiment-btn",
  },
  setTopicSummaryDate: {
    props: ["topicSummaryDate"],
    track: true,
    trackType: "research-topic-summary-date-selected",
    trackPayload: ({ topicSummaryDate }) => ({ date: topicSummaryDate.adjusted || topicSummaryDate.selectedDate }),
  },
  removeNonOverlappingItemsSuccess: {
    props: ["comparisonItems"],
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  removeNonOverlappingItems: ["items"],
  saveMetricToStore: {
    props: ["metric", "comparisonType"],
    track: true,
    trackType: "research-topic-graph-data-selected",
    trackPayload: ({ metric }) => ({ selected: metric }),
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  saveCompositionGroupToStore: {
    props: ["group"],
    track: true,
    trackType: "research-topics-composition-group-selected",
    trackPayload: ({ group }) => ({ group }),
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  saveTopicSummaryGroupToStore: {
    props: ["group"],
    track: true,
    trackType: "research-topics-summary-group-selected",
    trackPayload: ({ group }) => ({ group }),
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  trackPanelTrigger: {
    props: ["panel"],
    track: true,
    trackType: "research-topics-accordion-triggered",
    trackPayload: ({ panel }) => ({ accordion: panel }),
  },
  deleteExperiment: ["experiment"],
  deleteExperimentSuccess: [],
  deleteExperimentError: [],
  toggleSummaryPanel: ["experiment", "segment"],
  toggleRenameExperiment: ["experiment"],
  toggleHedgeItem: ["item"],
  loadCurrentHedges: ["currentHedges"],
  evaluateAs: {
    props: ["item"],
    track: true,
    trackType: "research-topic-summary-evaluate-as",
    trackPayload: {},
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  addHedge: {
    props: ["hedge"],
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  editHedge: {
    props: ["hedge"],
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  removeHedge: {
    props: ["hedge"],
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  applyHedgeOptions: {
    props: ["hedges"],
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  splitWithHedge: {
    props: ["item", "trackType"],
    track: true,
    trackType: ({ trackType }) => trackType,
    trackPayload: {},
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  loadSmartTrade: ["experiment"],
  toggleUpdateExperiment: {
    props: ["experiment"],
    track: true,
    trackType: "research-topic-update-experiment-btn",
  },
  setComparisonItems: {
    props: ["items"],
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  loadSnapshot: ["payload"],
  setSnapshot: ["snapshot"],
  addQuickHedge: ["item", "hedge"],
  navigateToPortfolio: ["portfolio"],
  toggleFxHedge: {
    props: ["item"],
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
})

const DEFAULT_STATE = {
  comparisonItems: [],
  hedges: [],
  selectedComparisonType: { id: "summary" },
  savedMetrics: {},
  savedCompositionGroup: "",
  savedTopicSummaryGroup: "",
  topicSummaryDate: null,
  selected: {
    experiment: null,
    segment: null,
  },
  summaryPanelOpen: false,
  experimentToUpdate: null,
  renameExperimentOpen: false,
  hedgeItem: null,
  refInstrumentEvaluateAs: "long",
  snapshot: null,
}

function handleSetSelectedComparisonType(state, { selectedComparisonType }) {
  return { ...state, selectedComparisonType }
}

function handleRemoveNonOverlappingItemsSuccess(state, { comparisonItems }) {
  return { ...state, comparisonItems }
}

function handleEvaluateAs(state, { item }) {
  if (item === "ref") {
    return { ...state, refInstrumentEvaluateAs: state.refInstrumentEvaluateAs === "long" ? "short" : "long" }
  }

  const itemIndex = findIndex(state.comparisonItems, elem => elem.id === item.id && elem.duplicate === item.duplicate)
  const comparisonItems = [...state.comparisonItems]
  comparisonItems[itemIndex].evaluateAs = item.evaluateAs === "long" ? "short" : "long"
  return { ...state, comparisonItems }
}

function handleSetTopicSummaryDate(state, { topicSummaryDate }) {
  return { ...state, topicSummaryDate: topicSummaryDate.adjusted || topicSummaryDate.selectedDate }
}

function handleSaveCompositionGroupToStore(state, { group }) {
  return { ...state, savedCompositionGroup: group }
}

function handleSaveTopicSummaryGroupToStore(state, { group }) {
  return { ...state, savedTopicSummaryGroup: group }
}

function handleLoadSavedSettings(state, { topicSettings }) {
  const savedSettings = {}

  if (topicSettings) {
    if (topicSettings.selectedComparisonType) {
      savedSettings.selectedComparisonType = topicSettings.selectedComparisonType
    }

    if (topicSettings.savedMetrics) {
      savedSettings.savedMetrics = topicSettings.savedMetrics
    }

    if (topicSettings.comparisonItems) {
      savedSettings.comparisonItems = topicSettings.comparisonItems
    }

    if (topicSettings.hedges) {
      savedSettings.hedges = topicSettings.hedges
    }

    if (topicSettings.savedCompositionGroup) {
      savedSettings.savedCompositionGroup = topicSettings.savedCompositionGroup
    }

    if (topicSettings.savedTopicSummaryGroup) {
      savedSettings.savedTopicSummaryGroup = topicSettings.savedTopicSummaryGroup
    }

    if (topicSettings.refInstrumentEvaluateAs) {
      savedSettings.refInstrumentEvaluateAs = topicSettings.refInstrumentEvaluateAs
    }
  }

  return { ...state, ...DEFAULT_STATE, ...savedSettings }
}

function handleSaveMetricToStore(state, { metric, comparisonType }) {
  const metrics = state.savedMetrics ? { ...state.savedMetrics } : {}

  metrics[comparisonType] = metric

  return { ...state, savedMetrics: metrics }
}

function handleDeleteExperimentSuccess(state) {
  return { ...state }
}

function handleDeleteExperimentError(state) {
  return { ...state }
}

function handleToggleRenameExperiment(state, { experiment }) {
  return {
    ...state,
    renameExperimentOpen: !state.renameExperimentOpen,
    experimentToUpdate: experiment,
  }
}

function handleApplyRenameExperiment(state, { updatedExperiment }) {
  const comparisonItems = cloneDeep(state.comparisonItems)
  const item = comparisonItems.find(({ id }) => id === updatedExperiment.id)
  if (item) {
    Object.assign(item, { label: updatedExperiment.name, description: updatedExperiment.description })
    return {
      ...state,
      renameExperimentOpen: false,
      experimentToUpdate: null,
      comparisonItems,
    }
  }
  return {
    ...state,
    renameExperimentOpen: false,
    selected: null,
  }
}

function handleToggleHedgeItem(state, { item }) {
  return { ...state, hedgeItem: item }
}

function handleToggleSummaryPanel(state, { experiment, segment }) {
  const { summaryPanelOpen, selected } = state

  if (!experiment) {
    return {
      ...state,
      summaryPanelOpen: false,
      selected: {
        experiment: null,
        segment: null,
      },
    }
  }

  if (summaryPanelOpen && selected) {
    if (selected.id !== experiment.id) {
      return { ...state, selected: { experiment, segment } }
    }
  } else {
    return {
      ...state,
      summaryPanelOpen: !state.summaryPanelOpen,
      selected: {
        experiment,
        segment,
      },
    }
  }
}

function handleDialogClose(state) {
  return {
    ...state,
    experimentToUpdate: null,
    hedgeItem: null,
  }
}

function handleApplyHedgeOptions(state, { hedges }) {
  return { ...state, hedges }
}

function handleAddHedge(state, { hedge }) {
  const hedgeId = hedge.duplicate || hedge.id
  if (some(state.hedges, (existing) => (existing.duplicate || existing.id) === hedgeId)) {
    return state
  }
  return { ...state, hedges: [...state.hedges, hedge] }
}

function handleEditHedge(state, { hedge }) {
  const existingHedgeIndex = findIndex(state.hedges, (existing) => (existing.duplicate || existing.id) === (hedge.duplicate || hedge.id))
  if (existingHedgeIndex === -1) { return { ...state } }

  const newHedges = [...state.hedges]
  newHedges.splice(existingHedgeIndex, 1, hedge)
  return { ...state, hedges: newHedges }
}

function handleRemoveHedge(state, { hedge }) {
  const newHedges = filter(state.hedges, (existing) => (existing.duplicate || existing.id) !== (hedge.duplicate || hedge.id))
  return { ...state, hedges: newHedges }
}

function handleSetComparisonItems(state, { items, pHedges }) {
  // refInstrument id must be taken into account for hedge matching
  const allItemIds = map(items, (item) => item.duplicate || item.id)

  // Remove refInstrument from items if its received
  // Pick only props necessary for the comparisonItem selector
  const comparisonItems = chain(items)
    .filter(({ isRefInstrument }) => !isRefInstrument)
    .map(item => ({
      ...pick(item, ["id", "duplicate", "segment", "evaluateAs", "fxHedgeEnabled"]),
      type: item.baseType || item.type, // For the ResearchTopic state, the only important type is the baseType. item.type gets mutated everytime the item selectors run
    }))
    .value()
  // Remove orphan hedges (no correspoding item with the hedge id)
  // Prop [duplicate] superseed [id] in uniqueness
  const hedges = chain(pHedges || get(state, "hedges", []))
    .filter(hedge => includes(allItemIds, (hedge.duplicate || hedge.id)))
    .value()
  return { ...state, comparisonItems, hedges }
}

function handleLoadSnapshot(state, { payload }) {
  const items = get(payload, "items", [])
  const pHedges = chain(items)
    .filter((item) => item.hedge)
    .map((item) => pick(item, ["id", "ticker", "hedge"]))
    .value()
  return handleSetComparisonItems({ ...state, snapshot: null }, { items, pHedges })
}

function handleSetSnapshot(state, { snapshot }) {
  return {
    ...state,
    snapshot,
  }
}

function handleToggleFxHedge(state, { item }) {
  const comparisonItems = map(state.comparisonItems, (comparisonItem) => {
    if (comparisonItem.id === item.id) {
      return { ...comparisonItem, fxHedgeEnabled: !comparisonItem.fxHedgeEnabled }
    }
    return comparisonItem
  })

  return { ...state, comparisonItems }
}

export default createReducer(DEFAULT_STATE, {
  [Types.SET_SELECTED_COMPARISON_TYPE]: handleSetSelectedComparisonType,
  [Types.SET_TOPIC_SUMMARY_DATE]: handleSetTopicSummaryDate,
  [Types.LOAD_SAVED_SETTINGS]: handleLoadSavedSettings,
  [Types.REMOVE_NON_OVERLAPPING_ITEMS_SUCCESS]: handleRemoveNonOverlappingItemsSuccess,
  [Types.SAVE_METRIC_TO_STORE]: handleSaveMetricToStore,
  [Types.SAVE_COMPOSITION_GROUP_TO_STORE]: handleSaveCompositionGroupToStore,
  [Types.SAVE_TOPIC_SUMMARY_GROUP_TO_STORE]: handleSaveTopicSummaryGroupToStore,
  [Types.DELETE_EXPERIMENT_SUCCESS]: handleDeleteExperimentSuccess,
  [Types.DELETE_EXPERIMENT_ERROR]: handleDeleteExperimentError,
  [Types.TOGGLE_SUMMARY_PANEL]: handleToggleSummaryPanel,
  [Types.TOGGLE_RENAME_EXPERIMENT]: handleToggleRenameExperiment,
  [Types.TOGGLE_HEDGE_ITEM]: handleToggleHedgeItem,
  [Types.APPLY_HEDGE_OPTIONS]: handleApplyHedgeOptions,
  [Types.ADD_HEDGE]: handleAddHedge,
  [Types.EDIT_HEDGE]: handleEditHedge,
  [Types.REMOVE_HEDGE]: handleRemoveHedge,
  [Types.SET_COMPARISON_ITEMS]: handleSetComparisonItems,
  [Types.EVALUATE_AS]: handleEvaluateAs,
  [SaveExperimentTypes.RENAME]: handleApplyRenameExperiment,
  [DialogTypes.CLOSE]: handleDialogClose,
  [Types.LOAD_SNAPSHOT]: handleLoadSnapshot,
  [Types.SET_SNAPSHOT]: handleSetSnapshot,
  [Types.TOGGLE_FX_HEDGE]: handleToggleFxHedge,
})

function* handleLoadSuccess(action) {
  const { comparisonItems, snapshot } = yield select(state => ({
    comparisonItems: getRawComparisonItems(state),
    snapshot: get(state, "modules.researchTopic.snapshot"),
  }))

  if (action.resource === "researchTopicPreferences") {
    const preferedResearchTopicSettings = getUserPreference(action.data, "researchTopicSettings", null)
    if (snapshot) {
      yield put(Creators.loadSnapshot(snapshot))
    } else {
      yield put(Creators.loadSavedSettings(preferedResearchTopicSettings))
    }
  } else if (action.resource === "notifications" && !isEmpty(action.data)) {
    const newComparisonItems = updateUploadingExperiments(comparisonItems, action.data)

    yield put(Creators.setComparisonItems(newComparisonItems))
  }
}

function* handleRemoveNonOverlappingItems({ items }) {
  const { comparisonItems } = yield select(state => ({
    comparisonItems: state.modules.researchTopic.comparisonItems,
  }))

  const newComparisonItems = filter(comparisonItems, item => findIndex(items, { id: item.id }) === -1)

  yield put(notificationCreators.add("error", null, `${items.map(item => item.label).join(", ")} cannot be loaded because there are no overlapping dates with the current compare selection.`))
  yield put(Creators.removeNonOverlappingItemsSuccess(newComparisonItems))
}

function* handleLoadNewExperiment({ newExperiment }) {
  const [comparisonItems, earliestDate, latestDate, { fxHedgeEnabled }] = yield all([
    select(getRawComparisonItems),
    select(getEarliestDate),
    select(getLatestDate),
    select(getInstrumentPortfolio),
  ])

  if (!some(comparisonItems, elem => elem.id === newExperiment.id)) {
    // If we don't have the maximum number of comparison items. (1 base item + 4 comparisons)
    if (comparisonItems.length < 4) {
      if (!newExperiment.uploading) {
        // If we have an experiment date and it is outside the current experiments date range.
        if (newExperiment.date && (newExperiment.date < earliestDate || newExperiment.date > latestDate)) {
          yield put(notificationCreators.add("info", null, `${newExperiment.name} dates have been adjusted to reflect data availability of other loaded experiments. To access ${newExperiment.name}'s full history, please remove other experiments.`))
        }
      }
      // Use setComparisonItems because if you use addComparisonItems, the components will get reloaded before the componentDidUpdate setParentComparisonItems function is called
      yield put(Creators.setComparisonItems([...comparisonItems, { ...newExperiment, fxHedgeEnabled }]))
    } else {
      yield put(notificationCreators.add("info", null, `${newExperiment.name} could not be loaded. Please remove an experiment from a column. This (and all saved experiments) can be selected by "Searching for an Existing Experiment" from the "Add Experiment" dialog.`))
    }
  }
}

function* startDeleteExperiment({ experiment }) {
  const { account } = yield select(state => ({
    account: state.global.selected.account,
  }))
  try {
    yield call(delApi, `/api/${account}/e/${experiment.id}`)
    yield put(SegmentCreators.removeComparisonItem(experiment))
    yield put(Creators.deleteExperimentSuccess(experiment.id))
    yield put(modelCreators.resetCollections(["topicExperiments"]))
    yield put(notificationCreators.add("success", null, "Experiment successfully deleted."))
  } catch (err) {
    yield put(Creators.deleteExperimentError(err))
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while deleting your experiment."))
  }
}

function* handleToggleUpdateExperiment({ experiment }) {
  const { account, researchTopic } = yield select(state => ({
    account: state.global.selected.account,
    researchTopic: getRawTopic(state),
  }))

  let url = `/${account}/t/${researchTopic.id}/`
  if (experiment.type === "SMART_TRADE") {
    url += `smart-trade/${experiment.id}/edit`
  } else {
    url += `experiments/${experiment.id}/upload`
  }
  const { navigate } = yield getContext("router")
  yield call(navigate, url)
}

function* handleUpdateSmartTrade({ updatedExperimentId }) {
  const { comparisonItems } = yield select(state => ({
    comparisonItems: getRawComparisonItems(state),
  }))

  let collectionsToReset = ["topicExperiments"]
  for (let i = 0; i < size(comparisonItems); i++) {
    if (comparisonItems[i].id === updatedExperimentId) {
      const index = i + 1
      collectionsToReset = concat(collectionsToReset, [
        `topicComparisonItemCoverage${index}`,
        `compareTopicSummaryPositionSet${index}`,
        `comparePerformancePositionSet${index}`,
        `comparePerformanceMetrics${index}`,
        `compareRiskPositionSet${index}`,
        `compareExposurePositionSet${index}`,
        `compareCompositionPositionSet${index}`,
      ])
    }
  }

  if (size(collectionsToReset) > 0) {
    yield put(modelCreators.resetCollections(collectionsToReset))
  }
}

function* handleUploadUpdate({ state: { updatedNotifications } }) {
  const { comparisonItems } = yield select(state => ({
    comparisonItems: getRawComparisonItems(state),
  }))

  const newComparisonItems = updateAlternativeExperimentProgress(comparisonItems, updatedNotifications)
  // do not update the items if nothing was updated
  if (newComparisonItems) {
    yield put(Creators.setComparisonItems(newComparisonItems))
  }
}

function* addQuickHedge({ item, hedge }) {
  const { hedges } = yield select((state) => ({
    hedges: state.modules.researchTopic.hedges,
  }))

  const id = uuid()
  const baseItem = {
    ...pick(item, ["id", "evaluateAs"]),
    duplicate: id,
    type: item.baseType,
    isComputedId: true, // This is to prevent segmentsRoot from re-computing ids
  }

  // Replace the hedge id by the duplicate which is more precise
  const newHedge = {
    ...hedge,
    id,
  }
  yield put(Creators.applyHedgeOptions([...hedges, newHedge]))
  yield put(SegmentCreators.addComparisonItem([baseItem], null, "research-topic-hedge-instrument"))
}

function* navigateToPortfolio({ portfolio }) {
  const { account } = yield select((state) => ({
    account: state.global.selected.account,
  }))
  const { navigate } = yield getContext("router")
  yield call(navigate, `/${account}/p/${portfolio}`)
}

function* handleSplitItem({ item }) {
  if (!item.hedge) {
    yield put(SegmentCreators.splitItem(item))
    return
  }
  const { hedges } = yield select((state) => ({
    hedges: state.modules.researchTopic.hedges,
  }))

  const long = {
    ...item,
    duplicate: uuid(),
    segment: {
      filters: [{
        [FILTERS.LONG_SHORT]: "LONG",
      }],
      normalization: NORMALIZATION.CONTRIBUTION,
    },
  }
  const short = {
    ...item,
    duplicate: uuid(),
    segment: {
      filters: [{
        [FILTERS.LONG_SHORT]: "SHORT",
      }],
      normalization: NORMALIZATION.CONTRIBUTION,
    },
  }

  const newHedges = [
    {
      ...item.hedge,
      id: long.duplicate,
    },
    {
      ...item.hedge,
      id: short.duplicate,
    },
  ]

  yield put(Creators.applyHedgeOptions([...hedges, ...newHedges]))
  yield put(SegmentCreators.addComparisonItem([long, short]))
}

export function* saga() {
  yield takeLatest(LoaderTypes.LOAD_SUCCESS, handleLoadSuccess)
  yield takeLatest(Types.REMOVE_NON_OVERLAPPING_ITEMS, handleRemoveNonOverlappingItems)
  yield takeLatest(Types.LOAD_NEW_EXPERIMENT, handleLoadNewExperiment)
  yield takeLatest(Types.DELETE_EXPERIMENT, startDeleteExperiment)
  yield takeLatest(Types.TOGGLE_UPDATE_EXPERIMENT, handleToggleUpdateExperiment)
  yield takeLatest(SaveExperimentTypes.UPDATE_META_AND_UPLOAD_SUCCESS, handleUpdateSmartTrade)
  yield takeLatest(ServerNotificationsTypes.UPDATE_SUCCESS, handleUploadUpdate)
  yield takeLatest(Types.ADD_QUICK_HEDGE, addQuickHedge)
  yield takeLatest(Types.NAVIGATE_TO_PORTFOLIO, navigateToPortfolio)
  yield takeLatest(Types.SPLIT_WITH_HEDGE, handleSplitItem)
}
